'use client';
import type { PaginationProps } from './data';

import React from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import queryString from 'query-string';

import SkeletonPagination from './PaginationSkeleton';
import { DOTS, usePagination } from './usePagination';

import { Center, HStack, Icon, IconButton, Link, Text } from '@/components';
import ArrowLeft from '@/svgs/ArrowLeft.svg';
import ArrowRight from '@/svgs/ArrowRight.svg';

const Pagination: PaginationProps = ({
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
  isLoading,
  scrollToId,
  replace,
}) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sortBy = searchParams.get('sortBy');
  const status = searchParams.get('status');
  const search = searchParams.get('search');
  const faq_category_id = searchParams.get('faq_category_id');
  const customer_full_name = searchParams.get('customer_full_name');
  const customer_phone_number = searchParams.get('customer_phone_number');
  const created_at = searchParams.get('created_at');
  const order_id = searchParams.get('order_id');
  const lastPage = paginationRange?.[paginationRange.length - 1] as number;

  return (
    <>
      {isLoading ? (
        <SkeletonPagination />
      ) : (
        <Center>
          <HStack>
            <Link
              replace={replace}
              href={`${pathname}?${queryString.stringify({
                page: currentPage > 1 ? currentPage - 1 : 1,
                ...(sortBy && { sortBy }),
                ...(status && { status }),
                ...(search && { search }),
                ...(faq_category_id && { faq_category_id }),
                ...(customer_full_name && { customer_full_name }),
                ...(customer_phone_number && { customer_phone_number }),
                ...(created_at && { created_at }),
                ...(order_id && { order_id }),
              })}${scrollToId ? scrollToId : ''}`}
            >
              <IconButton
                isDisabled={currentPage === 1}
                variant="ghost"
                fontWeight="normal"
                colorScheme="secondary"
                aria-label="previous"
                ml="5"
                icon={<Icon as={ArrowRight} fill="secondary.500" />}
              />
            </Link>
            {paginationRange?.map((pageNumber, index) => (
              <React.Fragment key={`${index}-${pageNumber}`}>
                {pageNumber === DOTS ? (
                  <Text>{DOTS}</Text>
                ) : (
                  <Link
                    href={
                      pageNumber === 1
                        ? `${pathname}?${queryString.stringify({
                            ...(sortBy && { sortBy }),
                            ...(status && { status }),
                            ...(search && { search }),
                            ...(faq_category_id && { faq_category_id }),
                            ...(customer_full_name && { customer_full_name }),
                            ...(customer_phone_number && {
                              customer_phone_number,
                            }),
                            ...(created_at && { created_at }),
                            ...(order_id && { order_id }),
                          })}${scrollToId ? scrollToId : ''}`
                        : `${pathname}?${queryString.stringify({
                            page: pageNumber,
                            ...(sortBy && { sortBy }),
                            ...(status && { status }),
                            ...(search && { search }),
                            ...(faq_category_id && { faq_category_id }),
                            ...(customer_full_name && { customer_full_name }),
                            ...(customer_phone_number && {
                              customer_phone_number,
                            }),
                            ...(created_at && { created_at }),
                            ...(order_id && { order_id }),
                          })}${scrollToId ? scrollToId : ''}`
                    }
                    key={`${pageNumber}-${currentPage}`}
                  >
                    <IconButton
                      variant={pageNumber === currentPage ? 'solid' : 'ghost'}
                      colorScheme="secondary"
                      bg={
                        pageNumber === currentPage
                          ? 'secondary.500'
                          : 'gray.300'
                      }
                      fontWeight="normal"
                      color={pageNumber === currentPage ? 'white' : 'black'}
                      icon={String(pageNumber)}
                      aria-label={`page ${pageNumber}`}
                    ></IconButton>
                  </Link>
                )}
              </React.Fragment>
            ))}
            <Link
              href={`${pathname}?${queryString.stringify({
                page: currentPage < lastPage ? currentPage + 1 : lastPage,
                ...(sortBy && { sortBy }),
                ...(status && { status }),
                ...(search && { search }),
                ...(faq_category_id && { faq_category_id }),
              })}${scrollToId ? scrollToId : ''}`}
            >
              <IconButton
                mr="5"
                isDisabled={currentPage === lastPage || totalCount === 0}
                variant="ghost"
                colorScheme="secondary"
                aria-label="next"
                fontWeight="normal"
                icon={<Icon as={ArrowLeft} fill="secondary.500" />}
              />
            </Link>
          </HStack>
        </Center>
      )}
    </>
  );
};

export default Pagination;
